import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { backendURL } from '../IPaddress';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '../CSS/CRUDfiles.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const TICKET_STATUS_SEARCH_FIELDS = ['name', 'description'];
const TICKET_STATUS_SORT_OPTIONS = [
  { key: 'name', label: 'Name', accessor: (s) => s.name?.toLowerCase() ?? '' },
];


const TicketStatusComponent = () => {
  const [ticketStatuses, setTicketStatuses] = useState([]);
  const [currentTicketStatus, setCurrentTicketStatus] = useState({
    name: '',
    description: '',
    color: ''
  });
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [ticketStatusToDelete, setTicketStatusToDelete] = useState(null);
  const { authState } = useAuth();
  const navigate = useNavigate();

  const {
    items: visibleTicketStatuses,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(ticketStatuses, {
    searchFields: TICKET_STATUS_SEARCH_FIELDS,
    sortOptions: TICKET_STATUS_SORT_OPTIONS,
  });

  useEffect(() => {
    if (!authState) {
      navigate('/login');
    }
  }, [authState, navigate]);

  useEffect(() => {
    fetchTicketStatuses();
  }, []);

  const fetchTicketStatuses = async () => {
    const response = await fetch(`${backendURL}/api/ticket-statuses`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    setTicketStatuses(data);
  };

  const handleFormChange = (e) => {
    setCurrentTicketStatus({ ...currentTicketStatus, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentTicketStatus.ticket_status_id) {
      await updateTicketStatus(currentTicketStatus.ticket_status_id, currentTicketStatus);
    } else {
      await createTicketStatus(currentTicketStatus);
    }
    setCurrentTicketStatus({ name: '', description: '', color: '' });
    setShowForm(false);
    fetchTicketStatuses();
  };

  const createTicketStatus = async (ticketStatusData) => {
    await fetch(`${backendURL}/api/ticket-statuses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ticketStatusData),
      credentials: 'include'
    });
  };

  const updateTicketStatus = async (ticketStatusId, ticketStatusData) => {
    await fetch(`${backendURL}/api/ticket-statuses/${ticketStatusId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ticketStatusData),
      credentials: 'include'
    });
  };

  const deleteTicketStatus = async () => {
    if (ticketStatusToDelete) {
      await fetch(`${backendURL}/api/ticket-statuses/${ticketStatusToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setTicketStatusToDelete(null);
      setShowModal(false);
      fetchTicketStatuses();
    }
  };

  const editTicketStatus = (ticketStatus) => {
    setCurrentTicketStatus(ticketStatus);
    setShowForm(true);
  };

  const handleCancel = () => {
    setCurrentTicketStatus({ name: '', description: '', color: '' });
    setShowForm(false);
  };

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentTicketStatus({ name: '', description: '', color: '' });
    }
    setShowForm(!showForm);
  };

  const ticketStatusForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="name">
          <Form.Label>Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter status name"
            name="name"
            value={currentTicketStatus.name}
            onChange={handleFormChange}
          />
        </Form.Group>
        <Form.Group controlId="color">
          <Form.Label>Color</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter status color"
            name="color"
            value={currentTicketStatus.color}
            onChange={handleFormChange}
          />
        </Form.Group>
        <Form.Group controlId="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            placeholder="Enter status description"
            name="description"
            value={currentTicketStatus.description}
            onChange={handleFormChange}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New Ticket Status"
        listLabel="Show Ticket Statuses List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="Ticket Statuses"
        form={ticketStatusForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="ticket status"
                nounPlural="ticket statuses"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search ticket statuses" placeholder="Name or description" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No ticket statuses have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleTicketStatuses.map((status) => (
          <ExpandableRow
            key={status.ticket_status_id}
            main={{ title: status.name, subtitle: status.description || 'No description provided.' }}
            flags={status.color ? (
              <span className="compact-pill" style={{ backgroundColor: status.color }}>
                {status.color}
              </span>
            ) : null}
            actions={(
              <>
                <Button size="sm" onClick={() => editTicketStatus(status)} aria-label="Edit ticket status">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setTicketStatusToDelete(status.ticket_status_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete ticket status"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteTicketStatus}
        bodyText="Are you sure you want to delete this ticket status?"
      />
    </div>
  );
};

export default TicketStatusComponent;
